import { useState } from "react";
import { Mail, X, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const getApiUrl = () => {
  if (typeof window !== "undefined" && window.location.hostname.includes("preview.emergentagent.com")) {
    return process.env.REACT_APP_BACKEND_URL || "";
  }
  return "";
};

const LeadCaptureCard = ({ creatorUsername, config }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState("");

  if (!config?.enabled) return null;

  const title = config.title || "¿Quieres recibir nuestro Top 10 de modelos?";
  const description = config.description || "Suscríbete a nuestro boletín gratis y recibe lo mejor cada semana.";
  const buttonText = config.buttonText || "Suscribirme gratis";

  const close = () => {
    if (submitting) return;
    setOpen(false);
    setTimeout(() => {
      setSuccess(false);
      setEmail("");
    }, 250);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Ingresa tu email");
      return;
    }
    setSubmitting(true);
    try {
      const apiUrl = getApiUrl();
      const res = await fetch(`${apiUrl}/api/creators/${creatorUsername}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (res.ok) {
        setSuccess(true);
        toast.success("¡Suscripción confirmada!");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo registrar");
      }
    } catch {
      toast.error("Error de red");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Subtle footer-style CTA */}
      <div className="mt-8 pt-6 border-t border-white/5" data-testid="lead-capture-section">
        <div className="text-center max-w-sm mx-auto">
          <div className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/5 border border-white/10 mb-3">
            <Mail className="w-4 h-4 text-white/50" />
          </div>
          <p className="text-white/80 text-sm font-medium mb-1">{title}</p>
          <p className="text-white/40 text-xs mb-4 leading-relaxed">{description}</p>
          <button
            onClick={() => setOpen(true)}
            data-testid="lead-capture-cta"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/10 hover:border-white/20 text-white/90 hover:text-white text-xs font-medium transition-all"
          >
            <Mail className="w-3.5 h-3.5" />
            {buttonText}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4 animate-in fade-in duration-200"
          onClick={close}
          data-testid="lead-capture-modal"
        >
          <div
            className="w-full sm:max-w-md bg-[#0d0d0d] border border-white/10 rounded-t-3xl sm:rounded-2xl p-6 relative animate-in slide-in-from-bottom-4 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={close}
              className="absolute top-4 right-4 p-1.5 text-white/50 hover:text-white rounded-lg hover:bg-white/5"
              data-testid="lead-capture-close"
            >
              <X className="w-5 h-5" />
            </button>

            {!success ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white/80" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg leading-tight">{title}</h3>
                  </div>
                </div>
                <p className="text-white/70 text-sm mb-5 leading-relaxed">{description}</p>

                <form onSubmit={submit} className="space-y-3">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <input
                      type="email"
                      placeholder="tucorreo@ejemplo.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="lead-email-input"
                      required
                      autoFocus
                      className="w-full pl-10 pr-3 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 outline-none focus:border-white/30 transition-colors text-sm"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="lead-submit-btn"
                    className="w-full py-3.5 bg-white text-black font-semibold rounded-xl hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                  >
                    {submitting ? (
                      <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        {buttonText}
                      </>
                    )}
                  </button>
                  <p className="text-center text-white/30 text-[10px] leading-relaxed pt-1">
                    Tus datos están seguros. No compartimos tu información con terceros.
                  </p>
                </form>

                {/* Telegram channel alternative */}
                <div className="mt-5 pt-5 border-t border-white/10">
                  <p className="text-white/60 text-xs text-center mb-3 leading-relaxed">
                    Aquí puedes ver las novedades sobre nuestras modelos
                  </p>
                  <a
                    href="https://t.me/+S4_GtOXQlE0zMjUx"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="lead-telegram-channel-btn"
                    className="w-full py-3 bg-[#229ED9] hover:bg-[#1d8ec3] text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
                    </svg>
                    Unirme al canal de Telegram
                  </a>
                </div>
              </>
            ) : (
              <div className="text-center py-4" data-testid="lead-success">
                <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 mx-auto flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-white font-bold text-xl mb-2">¡Suscripción confirmada!</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-5 whitespace-pre-line">
                  {config.rewardMessage || "Gracias por suscribirte. Pronto recibirás novedades."}
                </p>
                {config.rewardUrl && (
                  <a
                    href={config.rewardUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="lead-reward-link"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-black font-medium rounded-xl hover:bg-white/90 transition-colors text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Continuar
                  </a>
                )}
                <button
                  onClick={close}
                  className="block mx-auto mt-4 text-white/50 hover:text-white text-xs"
                >
                  Cerrar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default LeadCaptureCard;
